import type { PropertyRecord, EquityRelease, Div40Item, LowValuePool } from "../types/portfolio";
import type { InvestorProfile, Structure, PropertyAssetClass } from "../types/entities";
import type { BudgetSettings } from "../types/budget";
import type { AppSettings, FundingSource } from "../components/settings/SettingsPage";
import { safeNumber } from "./formatting";
import { CURRENT_TAX_RULES as RULES } from "./tax-rules-2025-26";
import { DIV43_RATE_BANDS, type Div43Band } from "./tax-rules-div43-bands";
import { EFFECTIVE_LIVES, type EffectiveLifeRow } from "../data/div40-effective-lives";
import type { DepreciationSchedule } from "../types/portfolio";
// B129 — prorateFactor SSOT for FY-of-sale partial rental contribution.
// Circular import: saleProrate.ts imports fyStartDate/fyEndDate/maxDate/minDate
// from this file, and this file imports prorateFactor from saleProrate. Both
// modules only use the imported values inside function bodies (not at module
// top level), so ESM evaluation order resolves cleanly at runtime.
import { prorateFactor } from "./saleProrate";
// B121 — beneficiary distribution SSOT.
import { resolveBeneficiaryDistribution } from "./beneficiaryResolution";
import type { ResolvedDistribution } from "./beneficiaryResolution";
// B125 — trust loss carry-forward SSOT.
import { applyEntityLossCarryForward } from "./trustLossCarryForward";
import type { LossCarryForwardResult } from "./trustLossCarryForward";

// ─── Tax calculations ─────────────────────────────────────────────────────────
//
// All year-specific values (brackets, LITO, Medicare thresholds, MLS tiers,
// HELP table) live in `tax-rules-2025-26.ts`. To roll over to a new FY,
// swap the import alias. See B116 / ARCHITECTURE §18 for the procedure.

/**
 * Resident income tax — walks the bracket table from low to high. Each
 * bracket carries `baseAtLower` (cumulative tax at the start of the bracket),
 * so we just add the marginal portion above the previous upper threshold.
 */
function baseTax(income: number): number {
  if (income <= 0) return 0;
  let prevUpper = 0;
  for (const b of RULES.incomeTax.brackets) {
    if (income <= b.upper) {
      return b.baseAtLower + (income - prevUpper) * b.rate;
    }
    prevUpper = b.upper;
  }
  return 0; // unreachable — last bracket has Infinity upper
}

export interface TaxEstimate {
  grossTax: number;
  medicareLevy: number;
  litoOffset: number;
  netTax: number;
}

/** LITO: max $700, phases out per the rules-file thresholds. */
function calcLito(income: number): number {
  const l = RULES.lito;
  if (income <= l.phaseOutLowerThreshold) return l.max;
  if (income <= l.phaseOutUpperThreshold) {
    return l.max - (income - l.phaseOutLowerThreshold) * l.phaseOutLowerRate;
  }
  if (income <= l.upperBoundary) {
    return l.midPointAmount - (income - l.phaseOutUpperThreshold) * l.phaseOutUpperRate;
  }
  return 0;
}

/**
 * Medicare levy (individual). Phase-in below the upper threshold; full rate
 * above. `medicareExempt` returns 0; `medicareReduction` applies the half-levy
 * (V1 simplification — full ATO logic depends on dependant status; see B117).
 */
function calcMedicareLevy(
  income: number,
  options?: { medicareExempt?: boolean; medicareReduction?: boolean },
): number {
  if (options?.medicareExempt) return 0;
  const m = RULES.medicareLevy;
  if (income <= m.singleLowerThreshold) return 0;
  if (options?.medicareReduction) return income * m.reducedRate;
  if (income <= m.singleUpperThreshold) {
    return (income - m.singleLowerThreshold) * m.phaseInRate;
  }
  return income * m.rate;
}

/** Estimated ATO income tax. Optional opts pass through Medicare flags. */
export function estimateIncomeTax(
  income: number,
  options?: { medicareExempt?: boolean; medicareReduction?: boolean },
): TaxEstimate {
  const taxable = Math.max(0, income);
  const grossTax = baseTax(taxable);
  const medicareLevy = calcMedicareLevy(taxable, options);
  const litoOffset = Math.min(calcLito(taxable), grossTax);
  const netTax = Math.max(0, grossTax - litoOffset + medicareLevy);
  return { grossTax, medicareLevy, litoOffset, netTax };
}

/** Bracket marginal rate as a percentage (e.g. 30 for 30%). */
export function calculateMarginalRate(income: number): number {
  for (const b of RULES.incomeTax.brackets) {
    if (income <= b.upper) return Math.round(b.rate * 1000) / 10;
  }
  return RULES.incomeTax.brackets[RULES.incomeTax.brackets.length - 1].rate * 100;
}

/**
 * 2025-26 HELP/HECS compulsory repayment in DOLLARS (not a rate).
 *
 * The 2025-26 ATO system is marginal: the first tier ($67,001–$125,000)
 * charges 15c per $1 above $67,000; the middle tier ($125,001–$179,285)
 * charges $8,700 plus 17c per $1 above $125,000; the top tier ($179,286+)
 * reverts to flat 10% × full income.
 *
 * Old AusProp API was `helpRepaymentRate(income)` returning a single rate
 * to multiply by full income — broken under the marginal scheme. Callers
 * needing an effective average rate compute `helpRepaymentAmount(income)
 * / income`. Callers needing the next-dollar marginal rate use
 * `helpMarginalRate(income)`.
 */
export function helpRepaymentAmount(income: number): number {
  if (income < RULES.help.minIncomeThreshold) return 0;
  for (const t of RULES.help.tiers) {
    if (income >= t.lower && income <= t.upper) {
      if (t.fullIncomeRate !== null) return income * t.fullIncomeRate;
      return t.fixedAtLower + (income - t.lower) * t.marginalRate;
    }
  }
  return 0;
}

/**
 * HELP MARGINAL rate at a given income — the rate applied to the next
 * dollar of repayment income. Used by `effectiveMarginalRate`. Returns a
 * decimal (e.g. 0.15 for the $67k–$125k tier).
 */
export function helpMarginalRate(income: number): number {
  if (income < RULES.help.minIncomeThreshold) return 0;
  for (const t of RULES.help.tiers) {
    if (income >= t.lower && income <= t.upper) {
      return t.fullIncomeRate ?? t.marginalRate;
    }
  }
  return 0;
}

/**
 * Medicare Levy Surcharge (MLS) rate. Bracket-stepped per the rules-file
 * tables. Returns rate as percentage (e.g. 1.5 for 1.5%). Returns 0 when
 * Medicare-exempt OR has private hospital cover OR income below the first
 * threshold.
 *
 * Family path: when `familyHouseholdIncome` is provided (caller has resolved
 * household combined income), evaluates against family thresholds with a
 * $1,500 uplift per dependent child after the first. Otherwise singles path.
 *
 * Single SSOT shared by `calculateEffectiveRate`, `effectiveMarginalRate`,
 * and `formatRateComponentsLabel` (B109).
 */
export function mlsRateForIncome(
  income: number,
  options?: {
    medicareExempt?: boolean;
    hasPrivateHospitalCover?: boolean;
    familyHouseholdIncome?: number;
    dependentChildren?: number;
  },
): number {
  if (income <= 0 && options?.familyHouseholdIncome == null) return 0;
  if (options?.medicareExempt) return 0;
  if (options?.hasPrivateHospitalCover) return 0;

  if (typeof options?.familyHouseholdIncome === "number") {
    const uplift = Math.max(0, (options.dependentChildren ?? 0) - 1)
      * RULES.mls.dependentChildUplift;
    for (const tier of RULES.mls.family) {
      if (options.familyHouseholdIncome <= tier.upper + uplift) return tier.rate * 100;
    }
    return 1.5;
  }

  for (const tier of RULES.mls.singles) {
    if (income <= tier.upper) return tier.rate * 100;
  }
  return 1.5;
}

/**
 * AVERAGE effective tax rate — total tax (income tax + Medicare + MLS + HELP)
 * divided by gross income, expressed as a percentage. Use for displaying
 * "what proportion of your income goes to tax" or for converting gross →
 * after-tax income (e.g. take-home pay calculations).
 *
 * NOT the right helper for tax-saving / deduction expressions —
 * a deduction reduces tax at the user's MARGINAL bracket, not at the
 * average rate. For tax-saving math, use {@link effectiveMarginalRate}
 * or {@link personalTaxSavingFromLoss}.
 */
export function calculateEffectiveRate(
  income: number,
  options?: {
    medicareExempt?: boolean;
    medicareReduction?: boolean;
    hasPrivateHospitalCover?: boolean;
    medicareLevy?: number;
    hasHECS?: boolean;
    familyHouseholdIncome?: number;
    dependentChildren?: number;
  }
): number {
  if (income <= 0) return 0;
  const exempt = options?.medicareExempt ?? false;
  const reduced = options?.medicareReduction ?? false;
  const levy = exempt
    ? 0
    : reduced
      ? RULES.medicareLevy.reducedRate
      : (options?.medicareLevy ?? 2.0) / 100;
  const mls = mlsRateForIncome(income, options) / 100;
  // B118 — 2025-26 HELP is marginal-on-income-above-threshold. Convert the
  // dollar amount back to an average-rate fraction for the calculateEffectiveRate
  // formula (which divides total tax by income).
  const helpAmount = options?.hasHECS ? helpRepaymentAmount(income) : 0;
  return (baseTax(income) + (levy * income) + (mls * income) + helpAmount) / income * 100;
}

/**
 * MARGINAL effective tax rate — the user's bracket MTR plus Medicare,
 * MLS and HELP repayment rate. This is the rate that applies to each
 * additional dollar of income or each additional dollar of deduction.
 *
 * Use for tax-saving expressions: a $1 deductible loss reduces tax by
 * `effectiveMarginalRate × $1`. Returns a percentage (e.g. 40.25 for
 * a $130k earner with no exemptions, no private hospital cover, no HECS).
 *
 * Composition:
 *   bracket MTR         from calculateMarginalRate(income)
 * + Medicare levy       2% by default; 0 if exempt; override via opts.medicareLevy
 * + Medicare Levy Surcharge — bracket-stepped 1.0/1.25/1.5% above income
 *                       thresholds when no private hospital cover and not exempt
 * + HELP marginal rate from helpMarginalRate(income), if hasHECS (B118)
 */
export function effectiveMarginalRate(
  income: number,
  options?: {
    medicareExempt?: boolean;
    medicareReduction?: boolean;
    hasPrivateHospitalCover?: boolean;
    medicareLevy?: number;
    hasHECS?: boolean;
    familyHouseholdIncome?: number;
    dependentChildren?: number;
  }
): number {
  if (income <= 0) return 0;
  const bracketMtr = calculateMarginalRate(income) / 100;
  const exempt = options?.medicareExempt ?? false;
  const reduced = options?.medicareReduction ?? false;
  const levy = exempt
    ? 0
    : reduced
      ? RULES.medicareLevy.reducedRate
      : (options?.medicareLevy ?? 2.0) / 100;
  const mls = mlsRateForIncome(income, options) / 100;
  // B118 — HELP marginal rate (next-dollar). 0.15 / 0.17 / 0.10 for
  // the three 2025-26 tiers; 0 below the $67k minimum income threshold.
  const helpRate = options?.hasHECS ? helpMarginalRate(income) : 0;
  return (bracketMtr + levy + mls + helpRate) * 100;
}

/**
 * B109 — Build a human-readable suffix listing the actual rate components
 * applied for a given profile / lens, based on the same flag inputs as
 * `effectiveMarginalRate`. Returns "" when no extra components apply.
 *
 * Used by the AI context emission and Dashboard per-adult tooltip so the
 * label matches the underlying math instead of always claiming
 * "+ Medicare/MLS/HELP" regardless of exemption state.
 *
 * Examples:
 *   { medicareExempt: true, hasHECS: false }                 → ""
 *   { medicareExempt: false, hasPrivateHospitalCover: true } → "Medicare 2%"
 *   income $200k, no exemptions                              → "Medicare 2% + MLS 1.5%"
 *   { medicareExempt: false, hasPrivateHospitalCover: true,
 *     hasHECS: true }                                        → "Medicare 2% + HECS"
 */
export function formatRateComponentsLabel(
  income: number,
  flags: {
    medicareExempt?: boolean;
    medicareReduction?: boolean;
    hasPrivateHospitalCover?: boolean;
    medicareLevy?: number;
    hasHECS?: boolean;
    familyHouseholdIncome?: number;
    dependentChildren?: number;
  },
): string {
  const parts: string[] = [];
  if (!flags.medicareExempt) {
    const levy = flags.medicareReduction
      ? RULES.medicareLevy.reducedRate * 100
      : (flags.medicareLevy ?? 2.0);
    parts.push(`Medicare ${levy}%`);
  }
  const mls = mlsRateForIncome(income, flags);
  if (mls > 0) parts.push(`MLS ${mls}%`);
  if (flags.hasHECS) parts.push("HECS");
  return parts.join(" + ");
}

/**
 * SSOT helper for "tax saving from a personally-claimable deductible amount".
 * Multiplies the deduction (rental loss, depreciation claim, equity-deductible)
 * by the user's effective marginal rate. The name says "FromLoss" because the
 * canonical use is negative-gearing rental losses, but it is correct for any
 * deduction that reduces personal taxable income (depreciation, borrowing
 * costs, work-related deductions).
 *
 * Returns dollars. Returns 0 when the input is non-positive.
 *
 * For losses quarantined within a trust/company (cannot offset personal
 * income), do NOT call this — those are unaffected by the personal MTR.
 */
/**
 * B116 — derive the family-MLS context (combined household income + dependent
 * children count) from a profile's `householdMembers` array. Returns `{}`
 * for single-adult households so callers transparently fall through to the
 * singles-MLS path. Exported so `buildPortfolioContext` (and any future
 * lens-aware caller) can reuse the same derivation.
 *
 * Family MLS thresholds apply to combined family income; the bracket MTR is
 * still per-individual. Hospital cover / Medicare exemption flags remain
 * per-person — V1 simplification (full ATO logic depends on whether ALL
 * family members hold complying cover).
 */
export function deriveFamilyMlsContext(
  profile: InvestorProfile,
): { familyHouseholdIncome?: number; dependentChildren?: number } {
  const members = profile.householdMembers ?? [];
  const partners = members.filter((m) => m.memberType !== "dependent");
  if (partners.length === 0) return {};
  const dependents = members.filter((m) => m.memberType === "dependent");
  const familyHouseholdIncome =
    (profile.annualPreTaxIncome ?? 0)
    + partners.reduce((s, m) => s + (m.annualPreTaxIncome ?? 0), 0);
  return { familyHouseholdIncome, dependentChildren: dependents.length };
}

export function personalTaxSavingFromLoss(
  personalLoss: number,
  profile: InvestorProfile,
): number {
  if (personalLoss <= 0) return 0;
  const rate = effectiveMarginalRate(profile.annualPreTaxIncome ?? 0, {
    medicareExempt: profile.medicareExempt,
    medicareReduction: profile.medicareReduction,
    hasPrivateHospitalCover: profile.hasPrivateHospitalCover,
    medicareLevy: profile.medicareLevy,
    hasHECS: profile.hasHECS,
    ...deriveFamilyMlsContext(profile),
  });
  return personalLoss * (rate / 100);
}

/**
 * CGT discount fraction by entity type per ITAA 1997 Div 115.
 *
 * - Personal individual (null / undefined entityType per B106 SSOT) = 50%
 * - Trust (incl. corporate trustee, "Company / Trust") = 50%
 *   (trust is the taxpayer; corporate trustee is a legal device —
 *    ATO QC 17166, s.115-215 ITAA 1997)
 * - SMSF = 33.33% (one-third — s.115-100)
 * - Company = 0% (no discount available to companies in own right)
 *
 * Caller is responsible for the 12-month holding test — this
 * function only returns the rate that would apply IF the holding
 * test passes.
 *
 * Caveat (out of scope, Phase 3+ doesn't model): if a trust
 * distribution lands on a corporate beneficiary, that beneficiary
 * grosses up and loses the discount. Modelling distribution chains
 * is deferred. Trust-level rate returned here is correct for the
 * trust calc; downstream beneficiary treatment is a separate
 * concern. Tab explainer (Phase 5) surfaces this caveat to user.
 *
 * Caveat (out of scope): if trustee is assessed under s99A (no
 * beneficiary specifically entitled), the discount is generally
 * lost. AusProp does not currently model s99A assessment.
 *
 * Authority: ATO RPG 2025 p62; ITAA 1997 Div 115 (s.115-100,
 * s.115-215); B106 personal-name SSOT.
 *
 * @see B130 Wave 2A design §2a
 */
export function cgtDiscountRate(
  entityType: string | null | undefined,
): number {
  // Personal-name property (B106 SSOT: structureId null → entityType null).
  // The EntityType union is "Trust" | "SMSF" | "Company" | "Company / Trust";
  // "Personal" is never a valid token in the logic layer per L40.
  if (entityType === null || entityType === undefined) return 0.5;

  switch (entityType) {
    case "Trust":
    case "Company / Trust":
      return 0.5;
    case "SMSF":
      return 1 / 3;
    case "Company":
      return 0;
    default:
      // Unknown entityType (defensive — should not occur given the
      // EntityType union + DB CHECK constraint from B106).
      return 0;
  }
}

/**
 * 12-month CGT holding period test (ITAA 1997 s.115-25).
 *
 * Returns true iff the asset was held for *more than* 12 months,
 * measured from contract date (acquisition CGT event) to contract
 * date (disposal CGT event) per ATO RPG 2025 p62. The exactly-12-month
 * boundary returns false — the rule is "more than 12 months", not
 * "at least 12 months" (CGT discount denied when held = 365 days).
 *
 * Both date strings are ISO YYYY-MM-DD; empty/invalid inputs return
 * false (defensive — the discount is denied rather than incorrectly
 * applied if dates are missing).
 *
 * Phase 3 of B130 Wave 2A exports this from calculations.ts so the
 * helper is a single SSOT for the Mark-As-Sold preview, the cgt_events
 * write path, the FY CGT aggregation in buildPortfolioContext (Phase 4),
 * and the calculations.test.ts ATO worked-example tests.
 *
 * @see B130 Wave 2A design §3
 */
export function isHeldOverTwelveMonths(
  purchaseDateStr: string,
  saleDateStr: string,
): boolean {
  if (!purchaseDateStr || !saleDateStr) return false;
  const purchase = new Date(purchaseDateStr + "T00:00:00");
  const sale = new Date(saleDateStr + "T00:00:00");
  if (isNaN(purchase.getTime()) || isNaN(sale.getTime())) return false;
  const anniversary = new Date(purchase);
  anniversary.setMonth(anniversary.getMonth() + 12);
  return sale.getTime() > anniversary.getTime();
}

// ─── B130 Wave 2B Phase 1 — Div 43 capital works derivation ───────────────────
//
// Pure functions consuming `DIV43_RATE_BANDS` (event-date-banded, see L48
// carve-out / L59). No UI imports. No type schema changes (Phase 2 adds
// `div43DateCommenced` / `div43RateOverridden` to `DepreciationSchedule`).
// No `buildPortfolioContext.ts` changes (Phase 6).
//
// Cross-refs: docs/audits/B130-wave-2b-design.md §2 (amended) and §3,
// docs/audits/B130-wave-2b-audit.md Notable Finding #1 + R1.6 + R13.

export interface Div43RateResult {
  /** Annual rate as decimal: 0, 0.025, or 0.04. */
  rate: number;
  /** Effective life: 0, 25, or 40. */
  lifeYears: number;
  /** False only for nil-case bands and unknown commenced dates. */
  eligible: boolean;
  /** Plain-English explanation surfaced to user + AI. */
  reason: string;
  /** Stable label, e.g. "Post-15-Sep-1987 standard". */
  bandLabel: string;
}

/**
 * Div 43 capital works rate + life lookup per RPG 2025 p51 (Table 1) and
 * p52 (Table 2). Rate is determined by construction COMMENCED date, not
 * completed.
 *
 * propertyClass:
 *   "residential"           — most AusProp users.
 *   "non-residential"       — commercial, retail, industrial.
 *   "short-term-traveller"  — hotels, motels, guesthouses with ≥10 rooms;
 *                             apartment buildings where owner/lessee holds
 *                             ≥10 apartments.
 *
 * flags.isBtR — eligible Build-to-Rent development, construction commenced
 * after 9 May 2023. Treasury Laws Amendment 2024.
 *
 * Implementation: thin lookup over `DIV43_RATE_BANDS`. Match-priority
 * order is encoded by the bands array (first match wins). See
 * `tax-rules-div43-bands.ts` for the data and `docs/audits/B130-wave-2b-design.md`
 * §2.1 for the priority spec.
 *
 * Returns rate 0 / eligible false when commencedDate is missing or no band
 * matches (defensive — the post-1987 standard catch-all should match any
 * post-1987 input).
 *
 * @see B130 Wave 2B design §2.2
 */
export function computeDiv43Rate(
  commencedDate: string | null | undefined,
  propertyClass: "residential" | "non-residential" | "short-term-traveller" = "residential",
  flags: { isBtR?: boolean } = {},
): Div43RateResult {
  if (!commencedDate) {
    return {
      rate: 0,
      lifeYears: 0,
      eligible: false,
      reason: "Construction commenced date required to determine Div 43 rate.",
      bandLabel: "Unknown",
    };
  }
  const band = DIV43_RATE_BANDS.find((b) =>
    matchDiv43Band(b, commencedDate, propertyClass, flags),
  );
  if (!band) {
    return {
      rate: 0,
      lifeYears: 0,
      eligible: false,
      reason: `No Div 43 rate band matched commencedDate=${commencedDate}.`,
      bandLabel: "Unmatched",
    };
  }
  return {
    rate: band.rate,
    lifeYears: band.lifeYears,
    eligible: band.eligible,
    reason: band.reason,
    bandLabel: band.bandLabel,
  };
}

// Internal — first-match band predicate. Tests exercise via computeDiv43Rate.
function matchDiv43Band(
  band: Div43Band,
  commencedDate: string,
  propertyClass: "residential" | "non-residential" | "short-term-traveller",
  flags: { isBtR?: boolean },
): boolean {
  if (band.requiresFlag === "isBtR" && !flags.isBtR) return false;
  if (!band.appliesTo.includes(propertyClass)) return false;
  if (band.commencedFrom !== null && commencedDate < band.commencedFrom) return false;
  if (band.commencedTo !== null && commencedDate > band.commencedTo) return false;
  return true;
}

/**
 * Boundary case detector — true when the completed date falls within
 * 12 months of any rate-band boundary. UI uses this to surface a
 * "confirm commenced date" prompt; commenced/completed often differ
 * for properties built across a boundary.
 *
 * Internals use UTC-anchored dates — Sydney DST transitions otherwise
 * shift T00:00:00-local timestamps by an hour and corrupt the day-diff
 * arithmetic.
 *
 * @see B130 Wave 2B design §2.3
 */
export function isDiv43BoundaryCase(completedDate: string | null | undefined): boolean {
  if (!completedDate) return false;
  const cDate = new Date(completedDate + "T00:00:00Z").getTime();
  if (isNaN(cDate)) return false;
  const boundaries = ["1979-08-22", "1984-08-22", "1987-09-16", "1992-02-27", "2023-05-10"];
  return boundaries.some((b) => {
    const bDate = new Date(b + "T00:00:00Z").getTime();
    return Math.abs(cDate - bDate) < 365 * 86400000;
  });
}

/** Internal — empty rate result for early-return paths. */
function emptyDiv43Rate(): Div43RateResult {
  return {
    rate: 0,
    lifeYears: 0,
    eligible: false,
    reason: "No Div 43 calculation available.",
    bandLabel: "Unknown",
  };
}

/** Returns 1 July of the FY's start year (UTC-anchored). fyLabel shape: "2024-25". */
export function fyStartDate(fyLabel: string): Date {
  const [start] = fyLabel.split("-");
  return new Date(`${start}-07-01T00:00:00Z`);
}

/** Returns 30 June of the FY's end year (UTC-anchored). fyLabel shape: "2024-25". */
export function fyEndDate(fyLabel: string): Date {
  const [start] = fyLabel.split("-");
  const startYear = parseInt(start, 10);
  const endYear = startYear + 1; // FY is always exactly 1 year.
  return new Date(`${endYear}-06-30T00:00:00Z`);
}

/** Returns the latest of the supplied dates. */
export function maxDate(...dates: Date[]): Date {
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}

/** Returns the earliest of the supplied dates. */
export function minDate(...dates: Date[]): Date {
  return new Date(Math.min(...dates.map((d) => d.getTime())));
}

/**
 * Pro-rated annual Div 43 deduction for a given financial year, accounting
 * for partial ownership periods within the FY (acquisition or disposal
 * mid-year), construction completion mid-year, and remaining depreciable
 * life. Uses contract dates per Wave 2A (settlement date is not the trigger).
 *
 * fyLabel: "2024-25" format (1 Jul to 30 Jun).
 *
 * Returns 0 (with rateResult populated) when:
 *   - cost is 0/missing.
 *   - rate is 0 (nil case or unknown commenced).
 *   - life fully written off before this FY.
 *   - no contract date supplied.
 *   - claim window (FY ∩ ownership ∩ remaining life) is empty.
 *
 * Override mechanic: `schedule.div43Rate` (when set non-zero) overrides
 * the derived rate for the claim figure. `rateResult` still reflects the
 * derived rate so the UI can surface "Manually set — derived would be X%".
 *
 * @see B130 Wave 2B design §3
 */
export function computeDiv43AnnualClaim(
  schedule: DepreciationSchedule,
  contractDate: string | null,
  saleContractDate: string | null,
  fyLabel: string,
  propertyClass: "residential" | "non-residential" | "short-term-traveller" = "residential",
  flags: { isBtR?: boolean } = {},
): {
  amount: number;
  daysHeldThisFY: number;
  rateResult: Div43RateResult;
  remainingYears: number;
} {
  const cost = schedule.div43ConstructionCost;
  const overridden = schedule.div43RateOverridden === true;
  const overrideRate = schedule.div43Rate;
  if (!cost || cost <= 0) {
    return {
      amount: 0,
      daysHeldThisFY: 0,
      rateResult: emptyDiv43Rate(),
      remainingYears: 0,
    };
  }

  const rateResult = computeDiv43Rate(
    schedule.div43DateCommenced ?? schedule.div43DateCompleted ?? null,
    propertyClass,
    flags,
  );
  // Override path: gated by div43RateOverridden (Phase 2 type contract).
  // When true and div43Rate is set (including 0), the override value wins
  // over the derived rate; rateResult stays derived so the UI can surface
  // "Manually set — derived would be X%". Pre-fix-pass logic gated on
  // `overrideRate && overrideRate > 0` which (a) treated any stored
  // div43Rate as an override and (b) silently dropped 0% overrides via
  // the && short-circuit.
  const rate = overridden && typeof overrideRate === "number"
    ? overrideRate
    : rateResult.rate;
  if (rate <= 0) {
    return { amount: 0, daysHeldThisFY: 0, rateResult, remainingYears: 0 };
  }

  // When user overrides rate, life follows the override per Div 43 mapping
  // (2.5% → 40y, 4% → 25y, 0% → 0y) — same lookup the UI uses
  // (TaxPage.tsx OVERRIDE_LIFE_BY_RATE; SSOT mirror — keep in sync).
  // Without this, writeOffEnd uses the derived life and the claim runs
  // past the legislated life of the override rate (e.g. override 4% on a
  // 1991-completed building was previously claiming until 2031 instead of
  // ending at 2016).
  const OVERRIDE_LIFE_BY_RATE: Record<number, number> = { 0.025: 40, 0.04: 25, 0: 0 };
  const lifeYears = overridden
    && typeof overrideRate === "number"
    && OVERRIDE_LIFE_BY_RATE[overrideRate] !== undefined
    ? OVERRIDE_LIFE_BY_RATE[overrideRate]
    : (rateResult.lifeYears > 0 ? rateResult.lifeYears : 40);
  const fyStart = fyStartDate(fyLabel);
  const fyEnd = fyEndDate(fyLabel);
  const completed = schedule.div43DateCompleted
    ? new Date(schedule.div43DateCompleted + "T00:00:00Z")
    : null;
  if (!completed || isNaN(completed.getTime())) {
    return { amount: 0, daysHeldThisFY: 0, rateResult, remainingYears: 0 };
  }

  const writeOffEnd = new Date(completed);
  // setUTCFullYear so the day arithmetic stays in UTC and isn't
  // perturbed by a Sydney DST clock shift between completion + write-off.
  writeOffEnd.setUTCFullYear(writeOffEnd.getUTCFullYear() + lifeYears);
  if (writeOffEnd <= fyStart) {
    return { amount: 0, daysHeldThisFY: 0, rateResult, remainingYears: 0 };
  }

  const ownershipStart = contractDate
    ? new Date(contractDate + "T00:00:00Z")
    : null;
  const ownershipEnd = saleContractDate
    ? new Date(saleContractDate + "T00:00:00Z")
    : null;
  if (!ownershipStart || isNaN(ownershipStart.getTime())) {
    return { amount: 0, daysHeldThisFY: 0, rateResult, remainingYears: 0 };
  }

  const claimStart = maxDate(fyStart, ownershipStart, completed);
  const claimEnd = minDate(fyEnd, writeOffEnd, ownershipEnd ?? fyEnd);
  // Strict `<` so claimStart === claimEnd (same-day overlap, e.g. contract
  // on the last day of an FY) counts as 1 day inclusive.
  if (claimEnd < claimStart) {
    return { amount: 0, daysHeldThisFY: 0, rateResult, remainingYears: 0 };
  }

  const daysHeldThisFY =
    Math.floor((claimEnd.getTime() - claimStart.getTime()) / 86400000) + 1;
  const annual = cost * rate;
  const amount = annual * (daysHeldThisFY / 365);
  const remainingYears = Math.max(
    0,
    Math.ceil((writeOffEnd.getTime() - fyEnd.getTime()) / (365 * 86400000)),
  );

  return { amount, daysHeldThisFY, rateResult, remainingYears };
}

// ─── Div 40 helpers (B130 Wave 2C) ────────────────────────────────────────────

/**
 * Constant-time lookup over the 260-row effective-lives table. Built once at
 * module load; assetCode is unique per Phase 0 generation rules. Sibling
 * pattern to Wave 2B's `DIV43_RATE_BANDS` (also static reference data, also
 * legislative-date-banded per L59).
 */
const EFFECTIVE_LIVES_BY_CODE: Map<string, EffectiveLifeRow> = new Map(
  EFFECTIVE_LIVES.map((row) => [row.assetCode, row]),
);

export interface EffectiveLifeLookupResult {
  /** Years from the column matching `acquisitionDate`. 0 when row is unknown,
   * capital-works-only, or the matched column has no value. Caller falls
   * back to user-typed life with `effectiveLifeOverridden=true`. */
  years: number;
  /** Which acquisition-date column was matched. Useful for UI hints
   * ("12 yrs effective life — Commissioner's, post-1 Jul 2019 acquisitions"). */
  column: "pre-2004" | "2004-2019" | "post-2019";
  /** The full row (for category, notes, traceability). Null if `assetCode`
   * not found OR row is `capitalWorksOnly` (Div 43, not Div 40 — per audit D8). */
  row: EffectiveLifeRow | null;
  /** True if any column on the matched row was the `'own_estimate'` sentinel
   * (per audit D7). UI greys out the autofill and falls back to user-typed
   * life with `effectiveLifeOverridden=true`. */
  isOwnEstimate: boolean;
}

/**
 * Resolve the Commissioner's effective life (years) for a Div 40 asset given
 * its `assetCode` and `acquisitionDate`. Per ATO RPG 2025 + TR 2024/3.
 *
 * Three acquisition-date columns:
 *  - `pre-2004`:    `acquisitionDate < 2004-07-01`
 *  - `2004-2019`:   `2004-07-01 ≤ acquisitionDate < 2019-07-01`
 *  - `post-2019`:   `acquisitionDate ≥ 2019-07-01`
 *
 * Boundary anchor: 1 July (start of FY). Boundaries `2004-07-01` and
 * `2019-07-01` are inclusive of the later column (i.e. `2004-06-30` →
 * `pre-2004`; `2004-07-01` → `2004-2019`). Matches RPG p38 wording
 * "from 1 July YYYY".
 *
 * Capital-works-only rows (per audit D8) return `years: 0, row: null`. The
 * Phase 5 picker greys these out with a "Div 43 capital works — use the
 * Div 43 schedule" hint and never selects them. This function is defensive
 * against a caller passing one through anyway.
 *
 * Unknown `assetCode` returns `years: 0, row: null`. Caller (Phase 5 form)
 * falls back to user-typed life. `assetCode` is undefined for legacy rows
 * + free-text user assets — those callers should not invoke this function
 * in the first place; defensive handling here is for resilience, not policy.
 *
 * Column-level null fallback chain: `post-2019 → 2004-2019 → pre-2004`. Only
 * triggers for the 4 single-column legacy sub-tables (e.g.
 * `table_air_conditioning_plant_pre2004` carries `elPre2004` only;
 * `table_5_air_conditioning` has `el_post2003` normalised across all 3 by
 * Phase 0). Per audit R14.C, Phase 0 already mapped values across columns
 * where the source table covered them — fallback handles the genuine null
 * case only.
 *
 * String-comparison date logic relies on ISO `yyyy-mm-dd` ordering matching
 * chronological order — same convention used by Wave 2B's `matchDiv43Band`.
 *
 * @see B130 Wave 2C design §3
 */
export function lookupEffectiveLife(
  assetCode: string,
  acquisitionDate: string,
): EffectiveLifeLookupResult {
  const row = EFFECTIVE_LIVES_BY_CODE.get(assetCode);

  // Unknown code OR capital-works-only row → no Div 40 life available.
  if (!row || row.capitalWorksOnly) {
    return { years: 0, column: "post-2019", row: null, isOwnEstimate: false };
  }

  // Resolve column by date. Fallback chain handles the genuine null case
  // where the source sub-table only populated one of the three columns.
  let column: EffectiveLifeLookupResult["column"];
  let years: number | null;

  if (acquisitionDate < "2004-07-01") {
    column = "pre-2004";
    years = row.elPre2004 ?? row.el2004to2019 ?? row.elPost2019;
  } else if (acquisitionDate < "2019-07-01") {
    column = "2004-2019";
    years = row.el2004to2019 ?? row.elPost2019 ?? row.elPre2004;
  } else {
    column = "post-2019";
    years = row.elPost2019 ?? row.el2004to2019 ?? row.elPre2004;
  }

  return {
    years: years ?? 0,
    column,
    row,
    isOwnEstimate: row.isOwnEstimate,
  };
}

// ─── Div 40 eligibility (B130 Wave 2C Phase 3) ────────────────────────────────

export interface Div40EligibilityResult {
  /** True if a Div 40 deduction is allowable. False blocks the claim entirely
   * (caller renders $0 + cost flows to CGT cost base via Phase 9 disposal). */
  eligible: boolean;
  /** Plain-English reason — surfaces in UI banner + AI emission per L63. */
  reason: string;
  /** When ineligible, names the rule. Null when eligible. */
  blockedBy: "second-hand-rule" | "non-residential" | "pre-1985-asset" | null;
  /** Exception flags the user could legitimately claim to override the block.
   * Empty array when the property + entity profile makes no exceptions
   * available. UI surfaces these as a dropdown on blocked items. */
  applicableExceptions: Array<
    | "installed-by-owner"
    | "new-from-developer-6mo"
    | "excluded-entity"
    | "ppor-pre-9-May-2017"
  >;
}

/**
 * Property fields read by the Div 40 eligibility engine. Defined as a subset
 * type rather than `PropertyRecord` so PPOR-converted-to-rental fields
 * (`pporConverted`, `pporFirstRentedDate`) — added in Phase 7 UI — can be
 * passed through ahead of their type-level addition.
 */
export interface Div40EligibilityProperty {
  assetClass: PropertyAssetClass;
  contractDate?: string;
  purchaseDate: string;
  /** PPOR-converted-to-rental indicator, set in Phase 7 UI. */
  pporConverted?: boolean;
  /** Date PPOR was first rented out, when `pporConverted=true`. */
  pporFirstRentedDate?: string;
  /** Construction completion date — drives `new-from-developer-6mo`. */
  div43DateCompleted?: string;
}

/**
 * Owner-structure subset read by the eligibility engine. `null` argument
 * value represents personal-name property (B106 / L40 SSOT — `structureId
 * IS NULL`). The `excludedEntityType` field is added by Phase 4
 * (structures.excluded_entity_type column).
 */
export interface Div40EligibilityOwnerStructure {
  /** Phase 4 column. NULL for individuals + trusts + SMSFs in scope today. */
  excludedEntityType?: string | null;
}

/**
 * ITAA 1997 s40-27 second-hand-rule eligibility check for a Div 40 asset.
 *
 * Rules (in priority order):
 *  1. Non-residential property (`assetClass !== "Residential"`) — second-hand
 *     rule does not apply; eligibility = true. Short-circuits.
 *  2. Pre-9-May-2017 property contracts — grandfathered; eligibility = true.
 *  3. Item not flagged second-hand (`isSecondHand !== true`) — eligibility = true.
 *  4. Excluded-entity exception — owner structure has `excludedEntityType` set
 *     (Phase 4 column). Currently no AusProp user qualifies (none of Trust /
 *     SMSF / Individual). Future commercial users may.
 *  5. Item carries `secondHandExceptionReason` matching one of the 4 valid
 *     exceptions AND the property meets that exception's gating conditions.
 *
 * Per audit D3 (L60 trap): routes via `assetClass`, NOT `propertyType`.
 *
 * Per audit D4: 2 personal + 5 trust + 1 SMSF in the live portfolio. Personal
 * is in scope (Nirimba). SMSF is NOT an excluded entity per s40-27(2).
 *
 * @see B130 Wave 2C design §2
 */
export function computeDiv40Eligibility(
  item: Div40Item,
  property: Div40EligibilityProperty,
  ownerStructure: Div40EligibilityOwnerStructure | null,
): Div40EligibilityResult {
  const SECOND_HAND_BOUNDARY = "2017-05-09";

  // Rule 1: Non-residential bypasses second-hand rule entirely.
  if (property.assetClass !== "Residential") {
    return {
      eligible: true,
      reason: `Second-hand rule does not apply — property is ${property.assetClass}.`,
      blockedBy: null,
      applicableExceptions: [],
    };
  }

  // Rule 2: Pre-9-May-2017 property contracts grandfathered.
  const effectiveContract = property.contractDate ?? property.purchaseDate;
  if (effectiveContract && effectiveContract < SECOND_HAND_BOUNDARY) {
    return {
      eligible: true,
      reason: "Property contract pre-dates 9 May 2017 — grandfathered.",
      blockedBy: null,
      applicableExceptions: [],
    };
  }

  // Rule 3: Item not second-hand → eligible.
  if (item.isSecondHand !== true) {
    return {
      eligible: true,
      reason: "New asset — installed or purchased by owner.",
      blockedBy: null,
      applicableExceptions: [],
    };
  }

  // Now we know: residential, post-9-May-2017, second-hand. Default = blocked.
  // Determine applicable exceptions (UI offers these as override dropdown).
  const isExcludedEntity = ownerStructure?.excludedEntityType != null;
  const exceptionReason = item.secondHandExceptionReason;

  const applicable: Div40EligibilityResult["applicableExceptions"] = [];
  if (isExcludedEntity) applicable.push("excluded-entity");
  if (
    item.acquisitionDate &&
    property.contractDate &&
    item.acquisitionDate > property.contractDate
  ) {
    applicable.push("installed-by-owner");
  }
  if (property.div43DateCompleted && item.acquisitionDate) {
    const completed = new Date(property.div43DateCompleted + "T00:00:00Z");
    const acquired = new Date(item.acquisitionDate + "T00:00:00Z");
    const sixMonths = 183 * 86400000;
    if (acquired.getTime() - completed.getTime() <= sixMonths) {
      applicable.push("new-from-developer-6mo");
    }
  }
  if (
    property.pporConverted &&
    property.pporFirstRentedDate &&
    property.pporFirstRentedDate > SECOND_HAND_BOUNDARY
  ) {
    applicable.push("ppor-pre-9-May-2017");
  }

  // Apply user-flagged exception if it's in the applicable set.
  if (exceptionReason && applicable.includes(exceptionReason)) {
    return {
      eligible: true,
      reason: `Eligible via exception: ${exceptionReason}.`,
      blockedBy: null,
      applicableExceptions: applicable,
    };
  }

  // Auto-apply excluded-entity (no user opt-in needed — entity-level flag).
  if (isExcludedEntity) {
    return {
      eligible: true,
      reason: "Eligible via excluded-entity exception (ITAA s40-27(2)).",
      blockedBy: null,
      applicableExceptions: applicable,
    };
  }

  // Blocked.
  return {
    eligible: false,
    reason:
      "Second-hand asset acquired with post-9-May-2017 residential property. " +
      "Cost flows to CGT cost base. ITAA s40-27.",
    blockedBy: "second-hand-rule",
    applicableExceptions: applicable,
  };
}

// ─── Div 40 decay + claim (B130 Wave 2C Phase 3) ──────────────────────────────

/**
 * Adjustable value (AV) at the start of a target FY for a Div 40 item.
 *
 * AV walks: cost − cumulative decline from acquisition FY through (target FY − 1).
 * For prime-cost: `cost × (1 − rate × yearsHeld)`, floored at 0.
 * For diminishing-value: `cost × (1 − rate)^yearsHeld`, floored at 0.
 *
 * Where `rate = method === "prime-cost" ? 1/effectiveLife : 2/effectiveLife`.
 *
 * Per audit R14.A O1: `effectiveLife` value is used regardless of override
 * status — the override flag is for UI display, the value is the truth.
 *
 * Returns 0 when:
 *  - No `acquisitionDate` (legacy free-text item — caller falls back to cost).
 *  - `effectiveLife <= 0`.
 *  - target FY ≤ acquisition FY (acquisition-FY opening AV = `cost` itself,
 *    handled by caller).
 *  - Item written off (cumulative decline exceeded cost).
 *
 * Pro-rata within the acquisition FY is handled by `computeDiv40AnnualClaim`,
 * not here. This function returns the FY-start opening AV cleanly.
 *
 * UTC-anchored throughout (Wave 2B Sydney-DST trap parity).
 *
 * @see B130 Wave 2C design §5
 */
export function computeOpeningAV(item: Div40Item, targetFyLabel: string): number {
  if (!item.acquisitionDate || item.effectiveLife <= 0) return 0;
  const fyStart = fyStartDate(targetFyLabel);
  const acquired = new Date(item.acquisitionDate + "T00:00:00Z");
  if (isNaN(acquired.getTime())) return 0;
  if (acquired >= fyStart) return item.cost; // acquisition FY → opening = cost

  // FY-aligned year count via FY-label difference (Phase 3 build correction
  // surfaced via L57 — spec used `Math.round((fyStart − acqFyEnd) / 365.25-day)`
  // which for target FY = acq FY + 1 rounds to 0 instead of 1, off-by-one in
  // the wrong direction; cumulative decline at start of target FY = acq FY + N
  // is N years per the spec's own intent statement). UTC month is 0-indexed:
  // months 0–5 (Jan–Jun) belong to FY whose start year is `year − 1`; months
  // 6–11 (Jul–Dec) belong to FY whose start year is `year`.
  const acqMonth = acquired.getUTCMonth();
  const acqFyStartYear =
    acqMonth < 6
      ? acquired.getUTCFullYear() - 1
      : acquired.getUTCFullYear();
  const targetFyStartYear = fyStart.getUTCFullYear();
  const yearsCompleted = targetFyStartYear - acqFyStartYear;
  if (yearsCompleted <= 0) return item.cost; // defensive — caller short-circuits

  if (item.method === "prime-cost") {
    const rate = 1 / item.effectiveLife;
    return Math.max(0, item.cost * (1 - rate * yearsCompleted));
  } else {
    const rate = 2 / item.effectiveLife;
    return Math.max(0, item.cost * Math.pow(1 - rate, yearsCompleted));
  }
}

export interface Div40AnnualClaimResult {
  /** Decline for the FY in dollars. 0 when blocked, written off, or out of window. */
  amount: number;
  /** AV at start of this FY (or `cost` if acquisition FY). */
  openingAV: number;
  /** AV at end of FY = `openingAV − amount` (floored at 0). */
  closingAV: number;
  /** Days held within this FY (acquisition / disposal pro-rata). */
  daysHeldThisFY: number;
  /** Method actually used (mirrors `item.method`; surfaced for UI/AI). */
  method: "prime-cost" | "diminishing-value";
  /** Eligibility from `computeDiv40Eligibility` — populated even when amount=0. */
  eligibility: Div40EligibilityResult;
}

/**
 * Annual Div 40 deduction for a single item in a given FY. Encapsulates:
 *  - eligibility short-circuit (blocked → amount=0, eligibility populated)
 *  - <$300 immediate deduction (full cost in acquisition FY, $0 elsewhere)
 *  - pool-allocated path (excluded — caller routes pool items through
 *    `computeLowValuePoolAnnualClaim`)
 *  - DV / PC multi-year decay
 *  - acquisition / disposal pro-rata within the FY
 *  - life-expiry mid-FY (claim ends when AV hits 0)
 *  - leap-year via `/365` (Wave 2B parity, not `/366`)
 *
 * Per audit R14.A O2 + L63: per-FY override does NOT live here — caller
 * applies `item.fyOverrides[fyLabel]` on top. This function returns the
 * derived amount; UI emits both per L63.
 *
 * UTC-anchored date math (Wave 2B parity). String-comparison ISO dates.
 *
 * @see B130 Wave 2C design §5
 */
export function computeDiv40AnnualClaim(
  item: Div40Item,
  property: Div40EligibilityProperty,
  ownerStructure: Div40EligibilityOwnerStructure | null,
  fyLabel: string,
  saleContractDate?: string | null,
): Div40AnnualClaimResult {
  const eligibility = computeDiv40Eligibility(item, property, ownerStructure);
  const empty: Omit<Div40AnnualClaimResult, "eligibility"> = {
    amount: 0,
    openingAV: 0,
    closingAV: 0,
    daysHeldThisFY: 0,
    method: item.method,
  };

  if (!eligibility.eligible) return { ...empty, eligibility };
  if (item.poolAllocated) return { ...empty, eligibility }; // pool path
  if (!item.acquisitionDate || item.effectiveLife <= 0) {
    return { ...empty, eligibility };
  }

  const acquired = new Date(item.acquisitionDate + "T00:00:00Z");
  if (isNaN(acquired.getTime())) return { ...empty, eligibility };

  const fyStart = fyStartDate(fyLabel);
  const fyEnd = fyEndDate(fyLabel);
  const ownershipEnd = saleContractDate
    ? new Date(saleContractDate + "T00:00:00Z")
    : null;

  // <$300 immediate deduction (s40-80(2)).
  if (item.immediateDeductionTaken && item.cost < 300) {
    const acqFyEndYear =
      acquired.getUTCMonth() < 6
        ? acquired.getUTCFullYear()
        : acquired.getUTCFullYear() + 1;
    const acqFyEnd = new Date(`${acqFyEndYear}-06-30T00:00:00Z`);
    const inAcqFY = acqFyEnd >= fyStart && acqFyEnd <= fyEnd;
    if (inAcqFY) {
      return {
        amount: item.cost,
        openingAV: item.cost,
        closingAV: 0,
        daysHeldThisFY: 365, // immediate → not pro-rated
        method: item.method,
        eligibility,
      };
    }
    return { ...empty, eligibility }; // post-acquisition FYs: zero
  }

  // Standard DV / PC path.
  const openingAV =
    acquired >= fyStart ? item.cost : computeOpeningAV(item, fyLabel);
  if (openingAV <= 0) return { ...empty, eligibility };

  const claimStart = maxDate(fyStart, acquired);
  const claimEnd = ownershipEnd && ownershipEnd < fyEnd ? ownershipEnd : fyEnd;
  if (claimEnd < claimStart) return { ...empty, eligibility };

  const daysHeldThisFY =
    Math.floor((claimEnd.getTime() - claimStart.getTime()) / 86400000) + 1;

  // Annual rate. PC: rate against cost. DV: rate against opening AV.
  const rate =
    item.method === "prime-cost" ? 1 / item.effectiveLife : 2 / item.effectiveLife;
  const annualBase = item.method === "prime-cost" ? item.cost : openingAV;
  const annual = annualBase * rate;
  // Cap at openingAV (life-expiry mid-FY).
  const amount = Math.min(annual * (daysHeldThisFY / 365), openingAV);
  const closingAV = Math.max(0, openingAV - amount);

  return {
    amount,
    openingAV,
    closingAV,
    daysHeldThisFY,
    method: item.method,
    eligibility,
  };
}

// ─── Low-value pool (B130 Wave 2C Phase 3) ────────────────────────────────────

export interface LowValuePoolAnnualClaimResult {
  /** Decline for the FY = `additionsThisFY × 0.1875 + openingBalance × 0.375`. */
  decline: number;
  /** Closing balance after decline. */
  closingBalance: number;
  /** First-year additions component (18.75% rate). For UI breakdown. */
  firstYearAdditionsDecline: number;
  /** Subsequent-year balance component (37.5% rate). For UI breakdown. */
  subsequentBalanceDecline: number;
}

/**
 * Low-value pool annual decline per ITAA s40-440.
 *  - First-year additions: 18.75% (half of 37.5% — half-year rule).
 *  - Opening balance (carried over): 37.5%.
 *
 * Pool persists per-property per-FY. `additionsThisFY` is sum of pool-allocated
 * item costs whose `poolAllocationDate` falls in this FY. `openingBalance`
 * equals the previous FY's `closingBalance` (rolling).
 *
 * Per design §4: pool is per-property in V1 (per-structure aggregation
 * deferred). RPG p45 mandatory-pooling rule: any item <$1000 must be pooled
 * once any item is pooled — caller enforces in Phase 8 UI.
 *
 * @see B130 Wave 2C design §4
 */
export function computeLowValuePoolAnnualClaim(
  pool: LowValuePool,
): LowValuePoolAnnualClaimResult {
  const firstYearAdditionsDecline = pool.additionsThisFY * 0.1875;
  const subsequentBalanceDecline = pool.openingBalance * 0.375;
  const decline = firstYearAdditionsDecline + subsequentBalanceDecline;
  const closingBalance = Math.max(
    0,
    pool.openingBalance + pool.additionsThisFY - decline,
  );
  return {
    decline,
    closingBalance,
    firstYearAdditionsDecline,
    subsequentBalanceDecline,
  };
}

// ─── Div 40 balancing adjustment (B130 Wave 2C Phase 3) ───────────────────────

export interface Div40BalancingAdjustmentResult {
  /** Net adjustment in dollars (signed: + = income, − = deduction, 0 = no event). */
  adjustment: number;
  /** Classification for return routing. */
  type: "income" | "deduction" | "cost-base-only" | "none";
  /** Adjustable value at disposal date (= the AV at disposal, after walking decay). */
  adjustableValueAtDisposal: number;
  /** Plain-English reason for UI / AI. */
  reason: string;
}

/**
 * Div 40 balancing adjustment on disposal (s40-285).
 *
 *  - `terminationValue > AV` → income (recapture).
 *  - `terminationValue < AV` → deduction (residual decline).
 *  - blocked items → `cost-base-only` (cost reduces CGT cost base; no
 *    income/deduction).
 *  - no `disposalDate` or item not eligible → `none`.
 *
 * Per audit D13: caller persists the result to `cgt_events.div40_*` columns
 * (Phase 9). This function is the SSOT.
 *
 * @see B130 Wave 2C design §6
 */
export function computeDiv40BalancingAdjustment(
  item: Div40Item,
  eligibility: Div40EligibilityResult,
  disposalDate: string,
): Div40BalancingAdjustmentResult {
  if (!eligibility.eligible) {
    // Blocked items: cost flows to CGT cost-base reduction. No income/deduction.
    return {
      adjustment: 0,
      type: "cost-base-only",
      adjustableValueAtDisposal: 0,
      reason: "Blocked item — cost reduces CGT cost base (s110-25).",
    };
  }
  if (!disposalDate || !item.acquisitionDate || item.effectiveLife <= 0) {
    return {
      adjustment: 0,
      type: "none",
      adjustableValueAtDisposal: 0,
      reason: "No disposal event or insufficient asset data.",
    };
  }

  // Compute AV at disposal — walk to disposal-FY start, then pro-rata to
  // disposal day within that FY.
  const disposal = new Date(disposalDate + "T00:00:00Z");
  if (isNaN(disposal.getTime())) {
    return {
      adjustment: 0,
      type: "none",
      adjustableValueAtDisposal: 0,
      reason: "Invalid disposal date.",
    };
  }

  // Disposal FY label for opening AV walk.
  const disposalFyStartYear =
    disposal.getUTCMonth() < 6
      ? disposal.getUTCFullYear() - 1
      : disposal.getUTCFullYear();
  const disposalFyLabel = `${disposalFyStartYear}-${String(
    disposalFyStartYear + 1,
  ).slice(2)}`;
  const openingAV = computeOpeningAV(item, disposalFyLabel);

  // Decline accrued from FY-start to disposal date.
  const fyStart = fyStartDate(disposalFyLabel);
  const daysToDisposal =
    Math.floor((disposal.getTime() - fyStart.getTime()) / 86400000) + 1;
  const rate =
    item.method === "prime-cost" ? 1 / item.effectiveLife : 2 / item.effectiveLife;
  const annualBase = item.method === "prime-cost" ? item.cost : openingAV;
  const partialFYDecline = Math.min(
    annualBase * rate * (daysToDisposal / 365),
    openingAV,
  );
  const adjustableValueAtDisposal = Math.max(0, openingAV - partialFYDecline);

  const terminationValue = item.terminationValue ?? 0;
  const adjustment = terminationValue - adjustableValueAtDisposal;

  if (adjustment > 0) {
    return {
      adjustment,
      type: "income",
      adjustableValueAtDisposal,
      reason:
        `Termination value $${terminationValue.toFixed(0)} exceeds AV ` +
        `$${adjustableValueAtDisposal.toFixed(0)} — recapture (s40-285).`,
    };
  }
  if (adjustment < 0) {
    return {
      adjustment,
      type: "deduction",
      adjustableValueAtDisposal,
      reason:
        `Termination value $${terminationValue.toFixed(0)} below AV ` +
        `$${adjustableValueAtDisposal.toFixed(0)} — residual deduction (s40-285).`,
    };
  }
  return {
    adjustment: 0,
    type: "none",
    adjustableValueAtDisposal,
    reason: "Termination value equals adjustable value — no balancing event.",
  };
}

export function toAnnual(
  amount: number,
  frequency: "weekly" | "fortnightly" | "monthly" | "yearly"
): number {
  if (frequency === "weekly") return amount * 52;
  if (frequency === "fortnightly") return amount * 26;
  if (frequency === "monthly") return amount * 12;
  return amount;
}

// ─── Excluded expense categories — never sum into property opex ───────────────
//
// Mortgage repayments are excluded from expense calculations. Loan interest
// is already calculated separately from loanBalance × rate (in propertyMetrics
// and elsewhere). Including repayments as an expense row would:
//   1. Double-count interest (once from rate × balance, once from the row)
//   2. Incorrectly expense the principal component (principal repayments are
//      not deductible — they reduce debt, they don't reduce taxable income)
//
// Live data audit on Day 37 found every property had a "Mortgage repayments"
// row with `amount = monthlyExpenses` from the legacy defaultExpenseRows
// builder. Data has been cleaned via Supabase MCP and the builder updated.
// This filter is the structural guard so it cannot recur via import or
// manual entry.
export const EXCLUDED_EXPENSE_CATEGORIES = [
  "Mortgage repayments",
  "Mortgage Repayments",
  "Mortgage repayment",
  "Loan repayment",
  "Loan repayments",
] as const;

export function isExcludedExpenseCategory(category: string | undefined | null): boolean {
  if (!category) return false;
  return EXCLUDED_EXPENSE_CATEGORIES.some((c) => c.toLowerCase() === category.toLowerCase());
}

/**
 * Filter an array of expense-like rows, removing any that match the
 * excluded category list. Used by every consumer that sums expenseRows.
 * Generic over the row shape so it works with ExpenseRow, IncomeRow,
 * and any future type that has an optional `category` field.
 */
export function filterDeductibleExpenseRows<T extends { category?: string }>(rows: T[]): T[] {
  return rows.filter((r) => !isExcludedExpenseCategory(r.category));
}

export function monthlyRent(weeklyRent: number): number {
  return Math.round((safeNumber(weeklyRent) * 52) / 12);
}

export function monthlyFromFrequency(amount: number, frequency: string): number {
  const map: Record<string, number> = {
    Weekly: (amount * 52) / 12,
    Fortnightly: (amount * 26) / 12,
    Monthly: amount,
    Quarterly: amount / 3,
    Annual: amount / 12,
    "One-off": 0,
  };
  return map[frequency] ?? amount;
}

export function loanPaymentEstimate(balance: number, rate: number, years = 30): number {
  const monthlyRate = safeNumber(rate) / 100 / 12;
  const months = safeNumber(years) * 12;

  if (!monthlyRate) return safeNumber(balance) / Math.max(months, 1);

  return (safeNumber(balance) * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
}

/**
 * Per-tranche annual interest — uses the tranche's own rate × balance when
 * both are set (Day 37 extension: each equity release tranche can have its
 * own loan details). Falls back to release.amount × parent property rate
 * when per-tranche details aren't entered.
 */
function releaseAnnualInterest(r: { amount: number; balance?: number; rate?: number }, parentRate: number): number {
  if ((r.rate ?? 0) > 0 && (r.balance ?? 0) > 0) {
    return (r.balance ?? 0) * ((r.rate ?? 0) / 100);
  }
  return (r.amount ?? 0) * (parentRate / 100);
}

export function interEntityLoanInterest(properties: PropertyRecord[], structureId: string): number {
  // Sum interest across every formal-loan equity release tranche pointing
  // at the given destination structure, across every property. Uses per-
  // tranche rate/balance when set, else falls back to amount × parent rate.
  let total = 0;
  for (const p of properties) {
    const releases = p.equityReleases ?? [];
    for (const r of releases) {
      if (r.arrangement === "formal-loan" && r.destinationStructureId === structureId) {
        total += releaseAnnualInterest(r, p.rate ?? 0);
      }
    }
  }
  return total;
}

export function deductibleInterest(p: PropertyRecord): number {
  const annualInterest = annualLoanInterest(p);
  // Sum all informal-arrangement non-deductible interest — each tranche's
  // interest uses per-tranche rate/balance when set, else falls back to
  // amount × parent rate (s.8-1 ITAA 1997 use-of-funds rule).
  const informalReleases = (p.equityReleases ?? []).filter((r) => r.arrangement === "informal");
  if (informalReleases.length === 0) return annualInterest;
  const nonDeductible = informalReleases.reduce(
    (sum, r) => sum + releaseAnnualInterest(r, p.rate ?? 0),
    0
  );
  return Math.max(0, annualInterest - nonDeductible);
}

/**
 * ATO rule: loan establishment fees, legal costs, valuation fees, mortgage broker fees
 * and title search fees are deductible over min(5, loanTermYears) years.
 *
 * @param p         - the property whose loan has borrowing costs
 * @param fyStartDate - 1 July of the financial year being assessed
 */
export function calculateBorrowingCostDeduction(p: PropertyRecord, fyStartDate: Date): number {
  const costs = p.borrowingCosts ?? 0;
  const startDateStr = p.borrowingCostsStartDate ?? p.loanStartDate;
  if (!costs || !startDateStr) return 0;

  const startDate = new Date(startDateStr);
  if (isNaN(startDate.getTime())) return 0;

  const loanTerm = p.loanTermYears ?? 30;
  const amortPeriod = Math.min(5, loanTerm);
  const annualDeduction = costs / amortPeriod;

  // Amort window: [startDate, startDate + amortPeriod years)
  const amortEnd = new Date(startDate);
  amortEnd.setFullYear(amortEnd.getFullYear() + amortPeriod);

  // FY window: [fyStartDate, fyStartDate + 1 year)
  const fyEnd = new Date(fyStartDate);
  fyEnd.setFullYear(fyEnd.getFullYear() + 1);

  // No overlap → no deduction
  if (fyEnd <= startDate || fyStartDate >= amortEnd) return 0;

  return annualDeduction;
}

/**
 * Annual loan interest — the single source of truth for `loanBalance × rate`.
 *
 * Every consumer that needs per-property annual interest MUST call this helper
 * instead of inlining `(p.loanBalance ?? 0) * (p.rate ?? 0) / 100`. This was
 * extracted Day 37 after the loans audit found 10+ independent inline copies
 * of the same formula — the same pattern that caused the Day 36/37 consistency
 * bugs. Future improvements (offset balance subtraction, split-rate handling)
 * will only need to change this one function.
 *
 * Returns the GROSS annual interest on the property's loan. For the user's
 * ownership-adjusted share, multiply by `(p.ownershipPercent ?? 100) / 100`.
 * For the deductible portion (accounting for informal equity releases), use
 * `deductibleInterest(p)` which calls this internally.
 */
export function annualLoanInterest(p: PropertyRecord): number {
  // Subtract offset balance — interest is only charged on the net balance
  // after the offset. This is the canonical single-source-of-truth for
  // interest; all 10+ consumers inherit the offset-adjusted figure.
  const effectiveBalance = Math.max(0, (p.loanBalance ?? 0) - (p.offsetBalance ?? 0));
  const primaryInterest = effectiveBalance * ((p.rate ?? 0) / 100);
  // Split loan component (Day 37 — VISION #28). When splitLoan is true,
  // add the second component's interest. No offset subtraction on the
  // split balance (offset accounts typically apply to one loan only).
  const splitInterest = p.splitLoan && (p.splitLoanBalance ?? 0) > 0
    ? (p.splitLoanBalance ?? 0) * ((p.splitLoanRate ?? 0) / 100)
    : 0;
  // Equity release sub-loan interest (Day 38). Each tranche is a separate
  // loan account — releaseAnnualInterest uses r.balance × r.rate when both
  // are set, else falls back to r.amount × parentRate.
  const releaseInterest = (p.equityReleases ?? []).reduce(
    (s, r) => s + releaseAnnualInterest(r, p.rate ?? 0), 0
  );
  return primaryInterest + splitInterest + releaseInterest;
}

// ─── Loan repayment breakdown (J10 Stage 1) ─────────────────────────────────

export interface LoanRepaymentBreakdown {
  /** Annual interest portion (always tax-deductible for investment loans) */
  interest: number;
  /** Annual principal portion (0 during IO period) */
  principal: number;
  /** Annual total cashflow impact (interest + principal) */
  total: number;
  /** Whether the loan is currently in its interest-only period */
  isInIoPeriod: boolean;
  /** ISO date when IO period expires (null if not an IO loan or missing start date) */
  ioExpiryDate: string | null;
  /** True for IO loans where neither loanStartDate nor purchaseDate is set —
   *  IO expiry cannot be computed. Engine returns IO-forever as a conservative
   *  fallback. UI-only signal (LoansPage badge); other consumers can ignore. */
  dataMissing?: boolean;
}

/**
 * Returns the annual loan repayment breakdown for a property at a given date.
 *
 * - P&I loans: total = standard amortisation (interest + principal)
 * - IO loans during IO period: total = interest only (principal = 0)
 * - IO loans after IO expiry: total = P&I amortisation on remaining term
 *
 * IO expiry anchor: prefers loanStartDate; falls back to purchaseDate when
 * loanStartDate is empty. If both are empty on an IO loan, returns IO-forever
 * with `dataMissing: true` — UI should warn.
 *
 * Use .interest for tax/deductibility surfaces.
 * Use .total for cashflow/serviceability surfaces.
 *
 * Handles split loans by summing main + split contributions.
 * Handles offset balance by reducing balance used for interest calc.
 * Does NOT include equity release sub-loan repayments (those are separate facilities).
 */
export function annualLoanRepayment(
  p: PropertyRecord,
  atDate: Date = new Date(),
): LoanRepaymentBreakdown {
  const balance = p.loanBalance ?? 0;
  const rate = p.rate ?? 0;
  if (balance <= 0 || rate <= 0) {
    return { interest: 0, principal: 0, total: 0, isInIoPeriod: false, ioExpiryDate: null };
  }

  // Primary loan
  const effectiveBalance = Math.max(0, balance - (p.offsetBalance ?? 0));
  const primaryInterest = effectiveBalance * (rate / 100);
  const isIO = (p.repaymentType ?? "P&I").toUpperCase().includes("IO") || (p.repaymentType ?? "").toLowerCase().includes("interest");
  const ioYears = p.ioTermYears ?? 0;
  const totalYears = p.loanTermYears ?? 30;

  // IO expiry anchor: loanStartDate → purchaseDate → none (dataMissing)
  const anchorRaw = p.loanStartDate || p.purchaseDate || "";
  const anchor = anchorRaw ? new Date(anchorRaw) : null;
  const dataMissing = isIO && ioYears > 0 && !anchor;

  let ioExpiryDate: string | null = null;
  let isInIoPeriod = false;

  if (isIO && ioYears > 0 && anchor) {
    const expiry = new Date(anchor);
    expiry.setFullYear(expiry.getFullYear() + ioYears);
    ioExpiryDate = expiry.toISOString().slice(0, 10);
    isInIoPeriod = atDate < expiry;
  } else if (dataMissing) {
    // No anchor — assume still in IO period (conservative for cashflow:
    // doesn't pretend the loan has stepped up to P&I when we can't tell).
    isInIoPeriod = true;
  }

  let primaryTotal: number;
  if (isInIoPeriod) {
    // During IO: total = interest only
    primaryTotal = primaryInterest;
  } else {
    // P&I (or IO expired): amortise on remaining term
    const remainingYears = isIO && ioYears > 0 ? Math.max(1, totalYears - ioYears) : totalYears;
    const monthlyPayment = loanPaymentEstimate(effectiveBalance, rate, remainingYears);
    primaryTotal = monthlyPayment * 12;
  }
  const primaryPrincipal = Math.max(0, primaryTotal - primaryInterest);

  // Split loan contribution
  let splitInterest = 0;
  let splitPrincipal = 0;
  if (p.splitLoan && (p.splitLoanBalance ?? 0) > 0 && (p.splitLoanRate ?? 0) > 0) {
    const sb = p.splitLoanBalance ?? 0;
    const sr = p.splitLoanRate ?? 0;
    const sYears = p.splitLoanTermYears ?? 30;
    splitInterest = sb * (sr / 100);
    const splitIsIO = (p.splitLoanRepaymentType ?? "P&I").toUpperCase().includes("IO") || (p.splitLoanRepaymentType ?? "").toLowerCase().includes("interest");
    if (splitIsIO) {
      // Split component in IO: interest only
      splitPrincipal = 0;
    } else {
      const splitMonthly = loanPaymentEstimate(sb, sr, sYears);
      const splitTotal = splitMonthly * 12;
      splitPrincipal = Math.max(0, splitTotal - splitInterest);
    }
  }

  const totalInterest = primaryInterest + splitInterest;
  const totalPrincipal = primaryPrincipal + splitPrincipal;

  return {
    interest: totalInterest,
    principal: totalPrincipal,
    total: totalInterest + totalPrincipal,
    isInIoPeriod,
    ioExpiryDate,
    ...(dataMissing ? { dataMissing: true } : {}),
  };
}

export function lvr(balance: number, value: number): number {
  return value ? (balance / value) * 100 : 0;
}

export function grossYield(weeklyRent: number, currentValue: number): number {
  if (!currentValue || !weeklyRent) return 0;
  return (weeklyRent * 52) / currentValue * 100;
}

export function netYield(
  weeklyRent: number,
  currentValue: number,
  annualExpenses: number
): number {
  if (!currentValue || !weeklyRent) return 0;
  return ((weeklyRent * 52) - annualExpenses) / currentValue * 100;
}

export function usableEquity(value: number, balance: number, target = 0.8): number {
  return Math.max(0, value * target - balance);
}

/**
 * Per-property cashflow metrics. See ARCHITECTURE.md §7 "Co-ownership
 * convention" for the share-vs-gross rule:
 * - Gross fields (rent, loan, netCashflow, equity, requiredTopUp) reflect
 *   the FULL property as if 100% owned. Use these for per-property display.
 * - Share fields (rentShare, netCashflowShare, equityShare, requiredTopUpShare)
 *   apply the user's ownershipPercent and represent the user's contribution.
 *   Use these for portfolio aggregates and personal cashflow.
 * - lvr and usableEquity are intentionally NOT ownership-adjusted — they are
 *   property-level (bank) metrics, the same regardless of co-ownership.
 */
/** Total outstanding loan balance across primary + split + equity release sub-loans.
 *  Used for debt totals, LVR, and equity across every page. */
export function totalLoanBalance(p: PropertyRecord): number {
  return (p.loanBalance ?? 0)
    + (p.splitLoan ? (p.splitLoanBalance ?? 0) : 0)
    + (p.equityReleases ?? []).reduce((s, r) => s + (r.balance ?? 0), 0);
}

export function propertyMetrics(property: PropertyRecord) {
  const totalDebtOnProperty = totalLoanBalance(property);
  const equity = safeNumber(property.currentValue) - totalDebtOnProperty;
  const ownershipFactor = (safeNumber(property.ownershipPercent) || 100) / 100;

  // If the property has been set up with income/expense rows, use those for cashflow.
  const hasRows =
    (property.incomeRows?.length ?? 0) > 0 || (property.expenseRows?.length ?? 0) > 0;

  if (hasRows) {
    const annualIncome = (property.incomeRows ?? []).reduce(
      (s, r) => s + toAnnual(r.amount, r.frequency),
      0
    );
    // Filter out mortgage repayment rows — see EXCLUDED_EXPENSE_CATEGORIES.
    // Loan interest is computed separately below via annualLoanInterest / loanPaymentEstimate.
    const annualExpense = filterDeductibleExpenseRows(property.expenseRows ?? []).reduce(
      (s, r) => s + toAnnual(r.amount, r.frequency),
      0
    );
    // Explicit loan deduction — Day 37 fix. Previously this branch set
    // loan: 0 and relied on a "Mortgage repayments" expense row as a proxy.
    // That row has been excluded by filterDeductibleExpenseRows (Day 37
    // mortgage guard). Without this deduction, the hasRows branch would
    // show no loan cost at all — inflating cashflow by the full interest.
    // Uses the same formula as the flat-field branch below:
    //   IO → interest only (balance × rate / 12)
    //   P&I → full amortisation repayment (loanPaymentEstimate)
    // Primary loan monthly deduction
    const primaryLoanMonthly = (property.repaymentType ?? "") === "IO"
      ? (Math.max(0, (property.loanBalance ?? 0) - (property.offsetBalance ?? 0)) * ((property.rate ?? 0) / 100)) / 12
      : loanPaymentEstimate(property.loanBalance, property.rate, property.loanTermYears || 30);
    // Split loan component (Day 37)
    const splitLoanMonthly = property.splitLoan && (property.splitLoanBalance ?? 0) > 0
      ? ((property.splitLoanRepaymentType ?? "") === "IO"
        ? ((property.splitLoanBalance ?? 0) * ((property.splitLoanRate ?? 0) / 100)) / 12
        : loanPaymentEstimate(property.splitLoanBalance ?? 0, property.splitLoanRate ?? 0, property.splitLoanTermYears || 30))
      : 0;
    // Equity release sub-loan monthly repayments (Day 38). Only included
    // when the tranche has both balance and rate set (user entered details).
    const releaseLoanMonthly = (property.equityReleases ?? []).reduce((s, r) => {
      if (!r.balance || !r.rate) return s;
      const bal = Math.max(0, (r.balance ?? 0) - (r.offsetBalance ?? 0));
      if ((r.repaymentType ?? "IO") === "IO") {
        return s + (bal * ((r.rate ?? 0) / 100)) / 12;
      }
      return s + loanPaymentEstimate(bal, r.rate, r.loanTermYears || 30);
    }, 0);
    const loanMonthly = primaryLoanMonthly + splitLoanMonthly + releaseLoanMonthly;
    const rent = annualIncome / 12;
    const netCashflow = rent - annualExpense / 12 - loanMonthly;
    const requiredTopUp = Math.max(0, -netCashflow) + safeNumber(property.personalTopUp);
    return {
      rent,
      loan: loanMonthly,
      customExpenseMonthly: 0,
      vacancyAllowance: 0,
      maintenanceAllowance: 0,
      netCashflow,
      equity,
      lvr: lvr(totalDebtOnProperty, property.currentValue),
      usableEquity: usableEquity(property.currentValue, totalDebtOnProperty),
      requiredTopUp,
      // Co-ownership share fields (Day 36)
      ownershipFactor,
      rentShare: rent * ownershipFactor,
      netCashflowShare: netCashflow * ownershipFactor,
      equityShare: equity * ownershipFactor,
      requiredTopUpShare: requiredTopUp * ownershipFactor,
    };
  }

  // Fallback: flat-field calculation (original behaviour)
  const rent = monthlyRent(property.weeklyRent);
  const customExpenseMonthly = (property.customExpenses ?? []).reduce((sum, item) => {
    return sum + monthlyFromFrequency(item.amount, item.frequency);
  }, 0);
  const primaryLoan =
    property.repaymentType === "Interest Only"
      ? (safeNumber(property.loanBalance) * (safeNumber(property.rate) / 100)) / 12
      : loanPaymentEstimate(property.loanBalance, property.rate, property.loanTermYears || 30);
  // Split loan component
  const splitLoan = property.splitLoan && (property.splitLoanBalance ?? 0) > 0
    ? ((property.splitLoanRepaymentType ?? "") === "IO"
      ? ((property.splitLoanBalance ?? 0) * ((property.splitLoanRate ?? 0) / 100)) / 12
      : loanPaymentEstimate(property.splitLoanBalance ?? 0, property.splitLoanRate ?? 0, property.splitLoanTermYears || 30))
    : 0;
  // Equity release sub-loan monthly repayments (Day 38)
  const releaseMonthly = (property.equityReleases ?? []).reduce((s, r) => {
    if (!r.balance || !r.rate) return s;
    const bal = Math.max(0, (r.balance ?? 0) - (r.offsetBalance ?? 0));
    if ((r.repaymentType ?? "IO") === "IO") {
      return s + (bal * ((r.rate ?? 0) / 100)) / 12;
    }
    return s + loanPaymentEstimate(bal, r.rate, r.loanTermYears || 30);
  }, 0);
  const loan = primaryLoan + splitLoan + releaseMonthly;
  const vacancyAllowance = rent * 0.05;
  const maintenanceAllowance = safeNumber(property.annualMaintenance) / 12;
  const netCashflow =
    rent -
    safeNumber(property.monthlyExpenses) -
    customExpenseMonthly -
    loan -
    vacancyAllowance -
    maintenanceAllowance;
  const requiredTopUp = Math.max(0, -netCashflow) + safeNumber(property.personalTopUp);

  return {
    rent,
    loan,
    customExpenseMonthly,
    vacancyAllowance,
    maintenanceAllowance,
    netCashflow,
    equity,
    lvr: lvr(totalDebtOnProperty, property.currentValue),
    usableEquity: usableEquity(property.currentValue, totalDebtOnProperty),
    requiredTopUp,
    // Co-ownership share fields (Day 36)
    ownershipFactor,
    rentShare: rent * ownershipFactor,
    netCashflowShare: netCashflow * ownershipFactor,
    equityShare: equity * ownershipFactor,
    requiredTopUpShare: requiredTopUp * ownershipFactor,
  };
}

/**
 * Per-property holding status (cash position, Path A semantic).
 *
 * Returns BOTH gross and share-adjusted variants so callers pick the one that
 * matches their display context (ARCHITECTURE §7):
 * - Per-property displays (PropertyDetailPage, PortfolioPage table) use GROSS
 *   `netFortnightly` / `netAnnual`.
 * - Aggregates that sum across co-owners use SHARE-adjusted
 *   `netFortnightlyShare` / `netAnnualShare`.
 *
 * `status` always classifies on the SHARE — a 50% co-owner whose share is
 * positive should not be flagged as "needs top-up" just because the gross
 * gross figure is negative.
 *
 * Derived from `propertyMetrics(p).netCashflow`, which is MONTHLY. We multiply
 * by 12 and 12/26 here so callers receive correctly-unitised annual/fortnightly
 * values without re-deriving.
 */
export function propertyHoldingStatus(p: PropertyRecord): {
  status: "positive" | "supported";
  netFortnightly: number;
  netAnnual: number;
  netFortnightlyShare: number;
  netAnnualShare: number;
} {
  const m = propertyMetrics(p);
  const netAnnual = m.netCashflow * 12;
  const netFortnightly = netAnnual / 26;
  const netAnnualShare = netAnnual * m.ownershipFactor;
  const netFortnightlyShare = netFortnightly * m.ownershipFactor;
  return {
    // Status classification uses the SHARE — a co-owner whose share is
    // positive shouldn't be flagged as "needing top-up support".
    status: netFortnightlyShare >= 0 ? "positive" : "supported",
    netFortnightly,
    netAnnual,
    netFortnightlyShare,
    netAnnualShare,
  };
}

// ─── Equity release runway ───────────────────────────────────────────────────

function drawdownPeriodsPerMonth(freq: string | undefined): number {
  if (freq === "weekly") return 52 / 12;
  if (freq === "monthly") return 1;
  return 26 / 12; // default: fortnightly (most common AU pay cycle)
}

/** Simple runway: months until credit facility exhausted at current drawdown rate. */
export function equityReleaseRunwaySimple(release: EquityRelease): number | null {
  if (!release.creditLimit || !release.drawdownAmount || release.drawdownAmount <= 0) return null;
  const remaining = Math.max(0, release.creditLimit - (release.balance ?? 0));
  return remaining / (release.drawdownAmount * drawdownPeriodsPerMonth(release.drawdownFrequency));
}

/** Dynamic runway: accounts for rent growth reducing required drawdown over time. Cap at 360 months. */
export function equityReleaseRunwayDynamic(
  release: EquityRelease,
  entityNetCashflow: number,
  rentGrowthRate: number = 0.03
): number | null {
  if (!release.creditLimit || !release.drawdownAmount || release.drawdownAmount <= 0) return null;
  const periodsPerMonth = drawdownPeriodsPerMonth(release.drawdownFrequency);
  const monthlyDraw = release.drawdownAmount * periodsPerMonth;
  let remaining = Math.max(0, release.creditLimit - (release.balance ?? 0));
  let months = 0;
  let cashflow = entityNetCashflow;
  while (remaining > 0 && months < 360) {
    remaining -= monthlyDraw;
    months++;
    if (months % 12 === 0) cashflow *= (1 + rentGrowthRate);
  }
  return months;
}

// ─── Budget frequency helpers (short + long code support) ────────────────────

/** Convert a budget amount to monthly. Handles both short codes (W/F/M/Q/Y) and long forms. */
export function budgetAmountToMonthly(amount: number, freq: string): number {
  if (freq === "W" || freq === "Weekly") return (amount * 52) / 12;
  if (freq === "F" || freq === "Fortnightly") return (amount * 26) / 12;
  if (freq === "Q" || freq === "Quarterly") return amount / 3;
  if (freq === "Y" || freq === "Annual" || freq === "Yearly") return amount / 12;
  return amount; // Monthly or unknown → treat as monthly
}

// ─── PAYG variation amount ───────────────────────────────────────────────────

/** Compute annual PAYG variation amount from personally-claimable neg gearing.
 *  Returns both the auto-derived figure and the effective figure (respects toggle + override). */
export function annualPaygVariationAmount(
  properties: PropertyRecord[],
  // B106 — `structures` retained on the signature for backwards-compatible
  // callers, but no longer needed inside the body: isPersonalProperty(p)
  // reads from structureId IS NULL directly.
  _structures: Structure[],
  profile: InvestorProfile
): { autoDerived: number; effective: number } {
  // B129 — admit FY-of-sale properties; prorateFactor applied per-property
  // below. PAYG variation is always for the current FY — derive label inline.
  const now = new Date();
  const fyStartYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const fyLabel = `${fyStartYear}-${String(fyStartYear + 1).slice(2)}`;
  const active = properties.filter((p) => prorateFactor(p, fyLabel) > 0);

  // Compute personally-claimable rental loss (same formula as TaxPage + buildPortfolioContext)
  // B106 — `isPersonalProperty(p)` is the SSOT: structureId IS NULL is the
  // personal-name signal. Trust / SMSF / Company losses are quarantined.
  let personalLoss = 0;
  for (const p of active) {
    if (!isPersonalProperty(p)) continue;
    const ownershipFactor = (p.ownershipPercent ?? 100) / 100;
    // B129 — partial-FY pro-rata for FY-of-sale.
    const proRata = prorateFactor(p, fyLabel);
    const rent = ((p.incomeRows && p.incomeRows.length > 0)
      ? p.incomeRows.reduce((s, r) => s + toAnnual(r.amount, r.frequency), 0) * ownershipFactor
      : (p.weeklyRent ?? 0) * 52 * ownershipFactor) * proRata;
    const opex = ((p.expenseRows && p.expenseRows.length > 0)
      ? filterDeductibleExpenseRows(p.expenseRows).reduce((s, r) => s + toAnnual(r.amount, r.frequency), 0) * ownershipFactor
      : ((p.annualRates ?? 0) + (p.annualInsurance ?? 0) + (p.annualStrata ?? 0) + (p.annualPropertyManagement ?? 0)) * ownershipFactor) * proRata;
    const interest = annualLoanInterest(p) * ownershipFactor * proRata;
    const netRental = rent - opex - interest;
    if (netRental < 0) personalLoss += Math.abs(netRental);
  }

  const autoDerived = personalTaxSavingFromLoss(personalLoss, profile);
  const effective = !profile.paygVariationApproved ? 0
    : profile.paygVariationOverride != null ? profile.paygVariationOverride * 12
    : autoDerived;

  return { autoDerived, effective };
}

// ─── B121 — Trust beneficiary distribution allocation ──────────────────────

/**
 * B121 — Allocate trust net income across beneficiaries for a given FY.
 *
 * Returns one row per beneficiary with the resolved allocation. Caller
 * (TaxPage / buildPortfolioContext) joins these to per-adult tax positions
 * via `householdMemberId`.
 *
 * Sum of allocated amounts may differ from trustNetIncome if:
 *   - SMSF: returns 100% to single member regardless.
 *   - Trust loss: all rows = 0; remainder is quarantined for B125.
 *   - Sum < 100% (under-distributed): residual is undistributed, taxable to
 *     trustee at 47% per s.99A — flagged in `undistributedAmount` for the
 *     caller. NOT calculated as a beneficiary row in V1.
 *   - Sum > 100% (data error): allocations literal; caller surfaces warning.
 */
export type BeneficiaryAllocation = ResolvedDistribution & {
  beneficiaryId: string;
  beneficiaryName: string;
  householdMemberId?: string;
};

export function allocateTrustNetIncome(
  structure: Structure,
  fy: string,
  trustNetIncome: number,
): {
  allocations: BeneficiaryAllocation[];
  undistributedAmount: number;
  carryForward: LossCarryForwardResult;
} {
  const beneficiaries = structure.beneficiaries ?? [];

  // B125 — apply trust loss carry-forward BEFORE allocation.
  const carryForward = applyEntityLossCarryForward(structure, fy, trustNetIncome);
  const adjusted = carryForward.adjustedNetIncome;

  // B125 — special case: gross > 0 but losses absorbed everything.
  // All allocations = 0 with carry-forward-absorbed source.
  if (trustNetIncome > 0 && adjusted === 0 && carryForward.lossesAppliedThisFY > 0) {
    const allocations: BeneficiaryAllocation[] = beneficiaries.map((b) => ({
      percent: 0,
      amount: 0,
      actualDistributed: 0,
      source: "carry-forward-absorbed" as const,
      beneficiaryId: b.id,
      beneficiaryName: b.name,
      householdMemberId: b.householdMemberId,
    }));
    return { allocations, undistributedAmount: 0, carryForward };
  }

  const allocations: BeneficiaryAllocation[] = beneficiaries.map((b) => {
    const r = resolveBeneficiaryDistribution(
      b,
      fy,
      adjusted, // B125: pass POST-carry-forward income to resolver
      beneficiaries,
      structure.entityType,
    );
    return {
      ...r,
      beneficiaryId: b.id,
      beneficiaryName: b.name,
      householdMemberId: b.householdMemberId,
    };
  });

  // Sum may be < adjusted if user has set distributions but not 100%.
  // SMSF and trust-loss cases short-circuit; only relevant for discretionary
  // trusts with explicit distributions.
  if (structure.entityType === "SMSF" || adjusted < 0) {
    return { allocations, undistributedAmount: 0, carryForward };
  }
  const allocatedTotal = allocations.reduce((s, a) => s + a.amount, 0);
  const undistributedAmount = Math.max(0, adjusted - allocatedTotal);
  return { allocations, undistributedAmount, carryForward };
}

// ─── Portfolio funding summary ───────────────────────────────────────────────

export interface FundingSummaryBreakdown {
  id: string;
  type: FundingSource["type"] | "personal_contribution" | "internal_redistribution";
  label: string;
  monthlyAmount: number;
  linkedPropertyId?: string;
  linkedEquityReleaseId?: string;
  runwayMonths?: number | null;
  runwayMonthsDynamic?: number | null;
  derived?: boolean;
}

export interface FundingSummary {
  personalContribution: number;
  equityReleaseTotal: number;
  taxRefundTotal: number;
  internalRedistribution: number;
  totalFunding: number;
  totalCost: number;
  bufferAmount: number;
  totalCostWithBuffer: number;
  gap: number;
  breakdown: FundingSummaryBreakdown[];
}

// ─── Bucket waterfall allocation ──────────────────────────────────────────────

export interface BucketAllocation {
  bucket: import("../types/budget").BudgetBucket;
  requested: number;
  actual: number;
  fullyFunded: boolean;
  shortfall: number;
}

/**
 * Strict waterfall: iterate buckets in array order (= priority).
 * Each bucket receives min(requested, remaining surplus).
 * Returns per-bucket allocation and the property bucket's actual amount.
 */
export function computeBucketAllocations(
  buckets: import("../types/budget").BudgetBucket[],
  monthlySurplus: number,
): BucketAllocation[] {
  let remaining = Math.max(0, monthlySurplus);
  return buckets.map((b) => {
    const requested = b.monthlyAmount;
    const actual = Math.min(requested, remaining);
    remaining -= actual;
    return { bucket: b, requested, actual, fullyFunded: actual >= requested, shortfall: requested - actual };
  });
}

/** Single source of truth for the Cashflow page funding calculation.
 *  All downstream consumers (Dashboard, Strategy, AI context) derive from this. */
export function portfolioFundingSummary(
  properties: PropertyRecord[],
  structures: Structure[],
  budgetSettings: BudgetSettings,
  appSettings: AppSettings,
  _profile: InvestorProfile,
  monthlySurplus = 0,
): FundingSummary {
  const activeProps = properties.filter((p) => p.status !== "sold");

  // ── Personal contribution: actual allocation from waterfall (Phase 6c-3d) ──
  const allocations = computeBucketAllocations(budgetSettings.buckets, monthlySurplus);
  const propAlloc = allocations.find((a) => a.bucket.type === "property");
  const personalContribution = propAlloc?.actual ?? 0;

  // ── Equity release drawdowns: normalised to monthly ──
  const breakdown: FundingSummaryBreakdown[] = [];
  let equityReleaseTotal = 0;
  for (const p of activeProps) {
    for (const r of p.equityReleases ?? []) {
      if (!r.drawdownAmount || r.drawdownAmount <= 0) continue;
      const monthly = r.drawdownAmount * drawdownPeriodsPerMonth(r.drawdownFrequency);
      equityReleaseTotal += monthly;
      breakdown.push({
        id: r.id,
        type: "equity_release",
        label: `${p.nickname ?? p.address} — ${r.notes ?? `Release ${r.id.slice(0, 6)}`}`,
        monthlyAmount: monthly,
        linkedPropertyId: p.id,
        linkedEquityReleaseId: r.id,
        runwayMonths: equityReleaseRunwaySimple(r),
        runwayMonthsDynamic: equityReleaseRunwayDynamic(r, 0, appSettings.rentGrowthAssumption ?? 0.03),
      });
    }
  }

  // ── Tax refund: from PAYG variation (Phase 5) ──
  const paygAnnual = annualPaygVariationAmount(activeProps, structures, _profile);
  const taxRefundTotal = paygAnnual.effective / 12;

  if (taxRefundTotal > 0) {
    breakdown.push({
      id: "payg-variation",
      type: "tax_refund",
      label: "PAYG tax variation",
      monthlyAmount: taxRefundTotal,
      derived: _profile.paygVariationOverride == null,
    });
  }

  // ── Internal redistribution: surplus from entities with mode "redistribute" ──
  let internalRedistribution = 0;
  for (const s of structures) {
    if (s.redistributionMode !== "redistribute") continue;
    // B106 — the legacy `if (s.entityType === "Personal") continue;` guard
    // is removed. Post-migration there are no Personal-typed structures
    // (DB CHECK rejects them); personal-name properties have structure_id
    // IS NULL and aren't iterated by this loop.
    if (isSMSF(s.entityType) && !(appSettings.includeSMSFInPortfolio ?? true)) continue;
    const sProps = activeProps.filter((p) => p.structureId === s.id);
    const sCashflow = sProps.reduce((sum, p) => sum + propertyMetrics(p).netCashflowShare, 0);
    if (sCashflow > 0) {
      internalRedistribution += sCashflow;
      breakdown.push({
        id: `redist-${s.id}`,
        type: "internal_redistribution",
        label: `${s.displayName || s.entityType} surplus`,
        monthlyAmount: sCashflow,
      });
    }
  }

  // ── User-added funding sources from AppSettings ──
  let userSourcesTotal = 0;
  for (const fs of appSettings.fundingSources ?? []) {
    if (fs.type === "equity_release") continue; // equity releases come from property.equityReleases[]
    if (fs.monthlyAmount <= 0) continue;
    userSourcesTotal += fs.monthlyAmount;
    breakdown.push({
      id: fs.id,
      type: fs.type,
      label: fs.label,
      monthlyAmount: fs.monthlyAmount,
    });
  }

  const totalFunding = personalContribution + equityReleaseTotal + taxRefundTotal + internalRedistribution + userSourcesTotal;

  // ── Total cost: portfolio top-up (share-adjusted, per-entity netted) ──
  const structureIds = new Set(structures.map((s) => s.id));
  let totalCost = 0;
  for (const s of structures) {
    if (isSMSF(s.entityType) && !(appSettings.includeSMSFInPortfolio ?? true)) continue;
    const sProps = activeProps.filter((p) => p.structureId === s.id);
    const sCashflow = sProps.reduce((sum, p) => sum + propertyMetrics(p).netCashflowShare, 0);
    const sPersonalTopUp = sProps.reduce((sum, p) => sum + safeNumber(p.personalTopUp) * ((p.ownershipPercent ?? 100) / 100), 0);
    totalCost += Math.max(0, -sCashflow) + sPersonalTopUp;
  }
  // Unassigned properties
  const unassigned = activeProps.filter((p) => !structureIds.has(p.structureId ?? ""));
  if (unassigned.length > 0) {
    const uCashflow = unassigned.reduce((sum, p) => sum + propertyMetrics(p).netCashflowShare, 0);
    const uPersonalTopUp = unassigned.reduce((sum, p) => sum + safeNumber(p.personalTopUp) * ((p.ownershipPercent ?? 100) / 100), 0);
    totalCost += Math.max(0, -uCashflow) + uPersonalTopUp;
  }

  const bufferPercent = appSettings.portfolioBufferPercent ?? 10;
  const bufferAmount = totalCost * bufferPercent / 100;
  const totalCostWithBuffer = totalCost + bufferAmount;
  const gap = totalCostWithBuffer - totalFunding;

  return {
    personalContribution,
    equityReleaseTotal,
    taxRefundTotal,
    internalRedistribution,
    totalFunding,
    totalCost,
    bufferAmount,
    totalCostWithBuffer,
    gap,
    breakdown,
  };
}

/**
 * Portfolio totals SSOT (Dashboard, Strategy, Reports, Portfolio,
 * buildPortfolioContext all route through this).
 *
 * **Day 39 GROSS rule (preserved regardless of scope):**
 * `totalValue` and `totalDebt` are always GROSS — the property/loan exists
 * in full regardless of ownership split. Portfolio LVR derived from these
 * is therefore invariant to ownership.
 *
 * **`opts.scope`** controls only the share-adjusted fields
 * (`totalCashflow`, `totalTopUp`):
 * - `"individual"` (default — preserves pre-B103 behaviour): cashflow/topup
 *   use the user's individual share via `propertyMetrics().netCashflowShare`
 *   and `requiredTopUpShare` (= gross × `(ownershipPercent ?? 100) / 100`).
 * - `"household"`: cashflow/topup use the caller-provided
 *   `householdFactorFn(p)` — typically `householdFactorOf(p, structure, profile)`
 *   from `lib/householdScope.ts`. Caller passes the closure rather than us
 *   importing `householdFactorOf` directly, so calculations.ts stays pure
 *   math with no dependency on `householdScope.ts` (which itself depends
 *   on calculations.ts helpers like `annualLoanInterest`). Falls back to
 *   the individual factor silently if `householdFactorFn` is missing.
 *
 * Phase 4a wires the parameter through; Phase 4b/4c flip individual call
 * sites to `"household"` per the B103 rollout plan.
 */
export interface PortfolioSummaryOpts {
  scope?: "individual" | "household";
  /** Caller-supplied household-factor function (typically a closure over
   *  `householdFactorOf(p, structure, profile)`). Required when
   *  `scope === "household"`; ignored otherwise. */
  householdFactorFn?: (p: PropertyRecord) => number;
}

export function portfolioSummary(
  properties: PropertyRecord[],
  opts?: PortfolioSummaryOpts,
) {
  const scope = opts?.scope ?? "individual";
  const householdMode = scope === "household" && !!opts?.householdFactorFn;

  return properties.reduce(
    (acc, p) => {
      const m = propertyMetrics(p);
      acc.totalValue += safeNumber(p.currentValue);
      acc.totalDebt += totalLoanBalance(p);
      if (householdMode) {
        const factor = opts!.householdFactorFn!(p);
        acc.totalCashflow += m.netCashflow * factor;
        acc.totalTopUp += m.requiredTopUp * factor;
      } else {
        acc.totalCashflow += m.netCashflowShare;
        acc.totalTopUp += m.requiredTopUpShare;
      }
      return acc;
    },
    {
      totalValue: 0,
      totalDebt: 0,
      totalCashflow: 0,
      totalTopUp: 0,
    }
  );
}

/** Returns true when the entity type is SMSF. Shared helper — import from
 *  here instead of defining local copies per page. */
export function isSMSF(entityType: string | undefined): boolean {
  return entityType === "SMSF";
}

/**
 * B106 — Personal-name properties: `structureId` IS NULL is the source of truth.
 *
 * A property is personal-name when it has no structure assigned. "Personal"
 * is not a legal entity — it's the absence of one. Trust / SMSF / Company /
 * Company-Trust are the only legal entity types in the data model.
 *
 * Handles both `null/undefined` AND `""` (the legacy mapper coercion at
 * supabaseStorage.ts:99 that converts DB null → empty string for TS).
 *
 * Use this everywhere that previously did `entityType === "Personal"` or
 * `structure?.entityType === "Personal"`. See L40 + B106 in BACKLOG.
 */
export function isPersonalProperty(p: PropertyRecord): boolean {
  return !p.structureId;
}

/**
 * UI-only display label for a property's entity classification. Returns
 * "Personal" as a UX label when `structureId` is null/empty — but logic
 * NEVER reads "Personal" as an entity type, only as a display string.
 *
 * For non-personal properties, prefers the structure's `displayName` if
 * set (user-friendly), falling back to the bare `entityType` token.
 *
 * Centralises the pattern that previously lived inline at TaxPage.tsx,
 * buildPortfolioContext.ts, and PropertyDetailPage.tsx as `?? "Personal"`
 * fallbacks.
 */
export function displayEntityLabel(
  p: PropertyRecord,
  structures: Structure[],
): string {
  if (isPersonalProperty(p)) return "Personal";
  const s = structures.find((st) => st.id === p.structureId);
  // Use `||` not `??` because the supabaseStorage mapper coerces NULL→""
  // for displayName. An empty-string displayName should fall through to
  // entityType, not return "".
  return s?.displayName || s?.entityType || "Personal";
}

// ─── Lifecycle engine — phase detection ──────────────────────────────────────

export function detectPortfolioPhase(
  properties: PropertyRecord[],
  _loans: unknown[],
  profile: InvestorProfile
): 'seed' | 'accumulation' | 'consolidation' | 'transition' | 'distribution' {
  // Respect manual override
  if (profile.phaseOverride && profile.portfolioPhase) {
    return profile.portfolioPhase;
  }

  const summary = portfolioSummary(properties);
  const portfolioLVR = summary.totalValue > 0
    ? (summary.totalDebt / summary.totalValue) * 100
    : 0;
  const monthlyCashflow = summary.totalCashflow;
  const count = properties.length;
  const hasCommercial = properties.some(
    (p) => p.assetClass === 'Commercial' || p.assetClass === 'Industrial'
  );

  // Distribution: cashflow positive overall AND LVR < 40%
  if (monthlyCashflow > 0 && portfolioLVR < 40) return 'distribution';

  // Transition: LVR < 50%, at least one commercial/industrial, 3+ properties
  if (portfolioLVR < 50 && hasCommercial && count >= 3) return 'transition';

  // Consolidation: LVR < 65% and 3+ properties
  if (portfolioLVR < 65 && count >= 3) return 'consolidation';

  // Accumulation: 2+ properties
  if (count >= 2) return 'accumulation';

  // Seed: default (0 or 1 property)
  return 'seed';
}

// ─── Lifecycle engine — constraint detection ──────────────────────────────────

export function detectCurrentConstraint(
  properties: PropertyRecord[],
  _loans: unknown[],
  _profile: InvestorProfile,
  maxLVR: number
): 'cashflow' | 'capital' | 'both' | 'neither' {
  const summary = portfolioSummary(properties);
  const monthlyCashflow = summary.totalCashflow;
  const deployableEquity = summary.totalValue * (maxLVR / 100) - summary.totalDebt;

  const cashflowProblem = monthlyCashflow < -2000;
  const capitalProblem = deployableEquity < 50000;

  if (cashflowProblem && capitalProblem) return 'both';
  if (cashflowProblem) return 'cashflow';
  if (capitalProblem) return 'capital';
  return 'neither';
}